import type { Tag } from '~/api/models/Tag';
import { FavoriteFile } from '~/composables/files/useFavorites';
import { BaseModel } from './BaseModel';

export type ConfigEditorKeyType = 'string' | 'bool' | 'int' | 'float' | 'select' | 'text';

/**
 * Admin-supplied metadata for one key inside a config file. Every field is
 * optional on the wire and has a backend fallback, so a key the admin never
 * described still renders (as free text, named after the key itself).
 *
 * NOTE: snake_case is deliberate. The API parser camelCases object keys but
 * returns ARRAYS untouched without recursing (see convertDataToCamelCase), so
 * everything nested inside the `config_editor` array keeps its wire casing in
 * both directions. That is also what keeps real config key names such as
 * `sv_hostname` from being mangled into `svHostname` on the way through.
 */
export interface ConfigEditorKey {
    /**
     * The key as the file spells it, section-qualified where the format has
     * sections. Held as a value rather than as an object key so ordering is the
     * list's, and so a numeric-looking name survives the round trip.
     */
    key: string;
    friendly_name: string | null;
    friendly_description: string | null;
    help_url: string | null;
    can_delete: boolean;
    type: ConfigEditorKeyType;
    options: string[] | null;
    default: string | null;
}

/**
 * One named group of keys within a file. Both this list and the key list inside
 * it are ordered by the admin, by dragging, so neither may be sorted for
 * display: the order IS the declaration.
 *
 * A null name is the ungrouped bucket; the customer editor gives it a generic
 * heading rather than inventing one.
 */
export interface ConfigEditorCategory {
    name: string | null;
    keys: ConfigEditorKey[];
}

export interface ConfigEditorFile {
    /** File path, directory glob, or a path containing {{env.VAR}} placeholders. */
    path: string;
    /** Editing is refused while the server runs: the game rewrites this file on boot. */
    server_offline: boolean;
    help_url: string | null;
    /**
     * Unpack values that are inline structs into the settings inside them.
     * Unreal games write their whole configuration as one key's value, which is
     * a single unusable text box without this.
     */
    expand_structs: boolean;
    categories: ConfigEditorCategory[];
}

/** One key found by parsing a pasted file, offered for the admin to describe. */
export interface ParsedConfigFileKey {
    /** How the editor addresses this key: section-qualified, and `#2`-suffixed past a repeat. */
    identity: string;
    key: string;
    section: string | null;
    value: string;
    occurrence: number;
    /** The key this one was unpacked out of, for a member of an inline struct. */
    parent: string | null;
    suggested_type: ConfigEditorKeyType;
}

export interface ParsedConfigFile {
    /** `raw` means nothing was recognised: the paste was not a config file we can read. */
    format: string;
    keys: ParsedConfigFileKey[];
}

export interface EggFirewallRuleEntry {
    protocol?: string;
    action?: string;
    destination_port?: string;
}

export interface EggFirewallFilterEntry {
    filter: string;
    settings: Record<string, unknown>;
}

export interface EggFirewallInfralyConfig {
    rules: EggFirewallRuleEntry[];
    filters: EggFirewallFilterEntry[];
}

export interface EggFirewallConfig {
    infraly?: EggFirewallInfralyConfig;
}

// One extra allocation a server of this egg is granted. The port is either an
// offset from the primary allocation, or any free port (the highest available on
// the IP, so it doesn't consume the low contiguous ports primaries use). It is
// optionally bound to an egg startup variable (by env_variable name; null =
// reserve the port only). A server template can override a given additional port
// with a fixed range (see AdditionalPortRange).
//
// `display` elects this port as the address shown to the server's owner, for games
// whose direct-connect address is not the game port. At most one port carries it;
// none means the primary allocation is the address.
export interface AdditionalPort {
    variable: string | null;
    type: 'offset' | 'any_free';
    offset?: number;
    display?: boolean;
}

// A server template's range override for one of an egg's additional ports. It
// targets the egg port by bound variable name (else by position) and replaces
// that port's offset/any_free rule with a fixed [from, to] range.
export interface AdditionalPortRange {
    variable: string | null;
    index: number;
    from: number;
    to: number;
}

export type AdditionalPortFailMode = 'continue' | 'stop';

export class Egg extends BaseModel {
    public name = '';
    // What the daemon queries a server with: a gamedig game type (`rust`) or a
    // `protocol-<name>` pseudo-type. Drives ONLY querying -- never feature gating,
    // which is tag-based. Empty falls back to the game tag's default on the backend.
    public gamedigIdentifier = '';

    public thumbnail: string | null = null;
    public canUpdate = false;
    public identifier = '';
    public favoriteFiles?: FavoriteFile[];
    public configEditor?: ConfigEditorFile[];

    // Switch-game requirement status (set when listing switchable games).
    public meetsMinimum = true;
    public meetsRecommended = true;
    public requiresNodeChange = false;
    public requirements: { minimum: Record<string, number | null>; recommended: Record<string, number | null> } | null = null;

    // Admin only props
    public id = -1;
    public legacyId: number | null = null;
    public description = '';
    public dockerImages: Record<string, string> = {};
    public startup = '';
    public configFrom?: number;
    public configLogs = '';
    public configFiles = '';
    public configStartup = '';
    public configStop = '';
    public customConfig = '';
    public wispignore = '';
    public consoleFilters = '';
    public firewallConfig: EggFirewallConfig | null = null;

    public scriptInstall = '';
    public scriptIsPrivileged = false;
    public scriptEntry = '';
    public scriptContainer = '';
    public copyScriptFrom?: number;

    public scriptUpdate = '';
    public scriptUpdateIsPrivileged = false;
    public scriptUpdateEntry = '';
    public scriptUpdateContainer = '';
    public copyUpdateScriptFrom?: number;

    public nestId = -1;
    public serversCount?: number;

    // Admin game-switching policy (Game Switching tab + switch-groups board).
    public switchDisabled = false;
    public switchGroupIds: string[] = [];
    public minCpu: number | null = null;
    public minMemory: number | null = null;
    public minDisk: number | null = null;
    public recommendedCpu: number | null = null;
    public recommendedMemory: number | null = null;
    public recommendedDisk: number | null = null;
    public allowedNodeIds: string[] | null = null;
    public allowedLocationIds: string[] | null = null;
    public allowCrossLocationSwitch = false;

    // Whether to offer a "Join via Steam" button. Which port it targets is the
    // egg's display port, not a setting of its own.
    public steamJoinButton = false;

    // Games that check their own public address at startup need the container to
    // egress from the server's allocation. Set here it applies to every server on
    // the egg and cannot be switched off per server.
    public forceOutgoingIp = false;

    // Extra allocations granted when provisioning a server of this egg, each an
    // offset from the primary or any free port, optionally bound to a variable.
    // The primary port range and any per-port range override live on the server
    // template, not the egg.
    public additionalPorts: AdditionalPort[] = [];
    // What happens when additionalPorts can't all be allocated.
    public additionalPortFailMode: AdditionalPortFailMode = 'continue';

    public sourceKey: string | null = null;
    public sourceId: string | null = null;
    public sourceLabel: string | null = null;
    public sourceUrl: string | null = null;
    public sourceViewUrl: string | null = null;
    public sourceHash: string | null = null;
    public sourceSyncedAt: string | null = null;
    public sourceDetached = false;
    public sourceUpdateAvailable = false;

    public exportData: { category?: string } | null = null;
    public canExport = false;

    getRouteName() {
        return 'egg';
    }

    getRouteID() {
        return this.id.toString();
    }

    public get tags(): Tag[] {
        return this.getRelationship('tags') || [];
    }
}
